import { TextCodes } from '@api/types/translation/TextCodes';
import { DropdownField, useWarrantyClaimContext } from '@components';
import { useBCProducts } from '@hooks';
import { Box, FormControl, styled, TextField } from '@mui/material';
import { t } from '@translations';
import { lazy, Suspense } from 'react';
import { Controller } from 'react-hook-form';

const LazyAutocomplete = lazy(() =>
  import('@mui/material/Autocomplete').then((module) => ({
    default: module.default as React.ComponentType<
      React.ComponentProps<typeof module.default<string, false, false, true>>
    >,
  }))
);

const StyledTextField = styled(TextField)`
  & .MuiInputBase-input {
    box-sizing: border-box;
  }
  & legend span {
    display: none;
  }
`;

export const WarrantyProductSelect = () => {
  const { form, isBuyerWorking, formValues, isReadOnly } = useWarrantyClaimContext();
  const selectedProductModel = form.watch('productModel');
  const selectedProductType = form.watch('productType') || formValues['productType'];
  const { productModels, productTypes, isLoading } = useBCProducts(selectedProductType);

  return (
    <Box display='flex' flexDirection='column'>
      <Controller
        name='productType'
        control={form.control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl sx={{ mt: 5 }} required>
            <DropdownField
              label={t(TextCodes.Warranty.Block.ProductType)}
              {...field}
              getValue={(value) => {
                field.onChange(value);
                form.setValue('productModel', '');
              }}
              value={field.value || ''}
              options={productTypes || []}
              required={!isReadOnly}
              error={!!form.formState.errors.productType}
              readOnly={isReadOnly}
              isLoading={isLoading}
              disabled={isLoading}
            />
          </FormControl>
        )}
      />
      {!isBuyerWorking && selectedProductType && (
        <Controller
          name='productModel'
          control={form.control}
          render={({ field }) => (
            <FormControl sx={{ mt: 5 }}>
              <Suspense fallback={<div>{t(TextCodes.Base.Loading)}</div>}>
                <LazyAutocomplete
                  options={productModels || []}
                  getOptionLabel={(option) => option}
                  value={selectedProductModel}
                  onInputChange={(_, newInputValue) => {
                    field.onChange(newInputValue);
                  }}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label={t(TextCodes.Warranty.Block.ProductModel)}
                      variant='outlined'
                      disabled={!selectedProductType || isLoading}
                      error={!!form.formState.errors.productModel}
                      InputProps={{
                        ...params.InputProps,
                        readOnly: isReadOnly,
                      }}
                    />
                  )}
                  freeSolo
                  disabled={isLoading}
                  blurOnSelect
                  readOnly={isReadOnly}
                />
              </Suspense>
            </FormControl>
          )}
        />
      )}
    </Box>
  );
};
